import { defineStore } from 'pinia';

import type { Unternehmen } from '@/types/Partner';
import { createEmptyUnternehmen } from '@/types/Partner';
import { useNutzerStore } from '@/shared/Nutzer/useNutzerStore';
import { watch } from 'vue';
import type { Gevo, GevoKanal, Kommunikation } from '@/shared/AvalKonfigurator/Kommunikation';
import { useSharedApi } from '@/shared/api/useSharedApi';
import { useAuftraggeberApi } from '@/auftraggeber/api/useAuftraggeberApi.ts';

interface State {
  unternehmen: Unternehmen;
  gevoEinstellungen: Partial<Record<Gevo, Kommunikation>>;
}

export const useNutzerUnternehmen = defineStore('NutzerUnternehmen', {
  state: (): State => ({
    unternehmen: createEmptyUnternehmen(),
    gevoEinstellungen: {},
  }),

  actions: {
    async ladeNutzerUnternehmen() {
      const nutzerStore = useNutzerStore();

      return new Promise<void>((resolve) => {
        const stop = watch(
          () => nutzerStore.nutzer.organisation,
          async () => {
            if (nutzerStore.nutzer.organisation !== '') {
              const nutzerUnternehmenId = nutzerStore.nutzer.organisation;
              const response =
                await useAuftraggeberApi().partner.findeAuftraggeberNachId(nutzerUnternehmenId);
              this.unternehmen = response;
              stop();
              resolve();
            }
          },
          {
            immediate: true,
          },
        );
      });
    },

    async ladeGevoEinstellung(gevo: Gevo) {
      const nutzerStore = useNutzerStore();

      this.gevoEinstellungen[gevo] =
        await useSharedApi().sharedAvalPartnerKonfigurator.holeAuftraggeberKommunikation(
          gevo,
          nutzerStore.nutzer.organisation,
        );

      return this.gevoEinstellungen[gevo];
    },

    getUnternehmenName(unternehmenId: string): string {
      if (unternehmenId === this.unternehmen.unternehmenId) {
        return this.unternehmen.name;
      }
      return this.unternehmen.tochterUnternehmen.filter((u) => u.unternehmenId === unternehmenId)[0]
        .name;
    },
  },

  getters: {
    isHybrid(state): boolean {
      return state.unternehmen.hybrid;
    },

    mutterUndToechterIds(state) {
      const ids: string[] = [];
      if (
        state.unternehmen.mutterUnternehmenId != null &&
        state.unternehmen?.mutterUnternehmen?.unternehmenId != null
      ) {
        ids.push(state.unternehmen.mutterUnternehmen.unternehmenId);
      }
      ids.push(state.unternehmen.unternehmenId);
      state.unternehmen.tochterUnternehmen.forEach((u) => {
        ids.push(u.unternehmenId);
      });
      return ids;
    },

    unternehmenId(state) {
      return state.unternehmen.unternehmenId;
    },

    nutztGevoKanal(state) {
      return (gevo: Gevo, kanal: GevoKanal) =>
        state.gevoEinstellungen[gevo]?.gevoKanalDto === kanal;
    },

    hatTochter(state) {
      return state.unternehmen.tochterUnternehmen?.length > 0;
    },
  },
});
